import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { useConfiguratorContext } from "@studio3dx/legacy-use-configurator/react";
import { Button } from "../ui/button";
import Image from "next/image";
import { cn } from "../../lib/utils";
import { useMediaQuery } from "../../hooks/use-media-query";

interface SelectionHelperDialogProps {}

interface ConflictData {
  sectionId: string;
  optionId: string;
  conflictOptionId: string;
}

function ConflictContent({
  className,
  allSections,
  allConflictingOptions,
  newOption,
  newOptionSection,
}: {
  className?: string;
  allSections: any[];
  allConflictingOptions: any[];
  newOption: any;
  newOptionSection: any;
}) {
  return (
    <div className={cn("grid grid-cols-1 gap-6 py-4", className)}>
      {/* Options being removed - show all conflicts */}
      <div>
        <p className="font-semibold text-red-600 mb-2">
          {allConflictingOptions.length === 1
            ? "This option will be removed:"
            : "These options will be removed:"}
        </p>

        <div className="space-y-3">
          {allConflictingOptions.map((conflictOption) => {
            // Find section for this conflict option
            const conflictOptionSection = allSections.find((section) =>
              section.options.some((opt: any) => opt.id === conflictOption.id)
            );

            return (
              <div
                key={conflictOption.id}
                className="bg-red-50 rounded-lg p-4 border border-red-200"
              >
                <div className="flex items-start gap-3">
                  {conflictOption?.metadata?.thumbnail && (
                    <div className="w-16 h-16 relative shrink-0 overflow-hidden rounded-md">
                      <Image
                        src={conflictOption.metadata.thumbnail}
                        alt={conflictOption.label}
                        fill
                        className="object-cover"
                      />
                    </div>
                  )}
                  <div className="grow">
                    <p className="text-base font-medium">
                      {conflictOption?.label}
                    </p>
                    {conflictOptionSection && (
                      <p className="text-sm text-gray-500">
                        From: {conflictOptionSection.label}
                      </p>
                    )}
                    {conflictOption?.metadata?.uiDescription && (
                      <p className="text-xs text-gray-500 mt-1">
                        {conflictOption.metadata.uiDescription}
                      </p>
                    )}
                  </div>
                  {conflictOption?.metadata?.uiColor && (
                    <div
                      className="w-6 h-6 rounded-full border border-gray-200 shrink-0"
                      style={{
                        backgroundColor: conflictOption.metadata.uiColor,
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Option being added */}
      <div>
        <p className="font-semibold text-green-600 mb-2">
          This option will be added:
        </p>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-start gap-3">
            {newOption?.metadata?.thumbnail && (
              <div className="w-16 h-16 relative shrink-0 overflow-hidden rounded-md">
                <Image
                  src={newOption.metadata.thumbnail}
                  alt={newOption.label}
                  fill
                  className="object-cover"
                />
              </div>
            )}
            <div className="grow">
              <p className="text-base font-medium">{newOption?.label}</p>
              {newOptionSection && (
                <p className="text-sm text-gray-500">
                  To: {newOptionSection.label}
                </p>
              )}
              {newOption?.metadata?.uiDescription && (
                <p className="text-xs text-gray-500 mt-1">
                  {newOption.metadata.uiDescription}
                </p>
              )}
            </div>
            {newOption?.metadata?.uiColor && (
              <div
                className="w-6 h-6 rounded-full border border-gray-200 shrink-0"
                style={{ backgroundColor: newOption.metadata.uiColor }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const SelectionHelperDialog: FunctionComponent<SelectionHelperDialogProps> = (
  props
) => {
  const configurator = useConfiguratorContext();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const [open, setOpen] = useState<boolean>(false);
  const [conflict, setConflict] = useState<ConflictData | null>(null);

  useEffect(() => {
    const handler = (data?: ConflictData) => {
      if (data) {
        setConflict(data);
        setOpen(true);
      }
    };

    configurator.callbacks.onIncompatible.on(handler);

    // Clean up the event listener
    return () => {
      configurator.callbacks.onIncompatible.off(handler);
    };
  }, [configurator.callbacks.onIncompatible]);

  const handleCancel = () => {
    setOpen(false);
  };

  const handleReplace = () => {
    if (!conflict) return;

    const { sectionId, optionId } = conflict;

    // Find sections for the conflicting option
    const allSections = configurator.getAllSections();

    // Find all conflicts for this option
    const allSelectedOptionIds = configurator.getAllSelectedOptionIds();
    const allConflicts = allSections
      .flatMap((section) => section.options)
      .filter(
        (option) =>
          option.excludes?.includes(optionId) && // Option excludes the one we want to select
          allSelectedOptionIds.includes(option.id) // And the option is currently selected
      );

    // Get the section for the option we want to select
    const targetSection = allSections.find(
      (section) => section.id === sectionId
    );
    if (!targetSection) return;

    // Create a new selection state
    const newSelection = { ...configurator.selectedOptions };

    // Remove all conflicting options from their respective sections
    for (const conflictOption of allConflicts) {
      // Find which section this conflicting option belongs to
      const conflictSection = allSections.find((section) =>
        section.options.some((opt) => opt.id === conflictOption.id)
      );

      if (conflictSection && newSelection[conflictSection.id]) {
        newSelection[conflictSection.id] = newSelection[
          conflictSection.id
        ].filter((id) => id !== conflictOption.id);
      }
    }

    // Add the new option to its section
    if (targetSection.multiple) {
      // For multiple selection sections, add to the array
      newSelection[sectionId] = [...(newSelection[sectionId] || []), optionId];
    } else {
      // For single selection sections, replace the array
      newSelection[sectionId] = [optionId];
    }

    // Update the state directly
    configurator.setSelectedOptions(newSelection);

    // Close the dialog
    setOpen(false);
  };

  if (!conflict) return null;

  // Find the option we want to add
  const allSections = configurator.getAllSections();
  const newOption = allSections
    .flatMap((section) => section.options)
    .find((option) => option.id === conflict?.optionId);

  // Get all conflicting options (not just the one in the conflict data)
  const allSelectedOptionIds = configurator.getAllSelectedOptionIds();
  const allConflictingOptions = allSections
    .flatMap((section) => section.options)
    .filter(
      (option) =>
        option.excludes?.includes(conflict.optionId) && // Option excludes the one we want to select
        allSelectedOptionIds.includes(option.id) // And the option is currently selected
    );

  // Get section for the new option for additional context
  const newOptionSection = allSections.find(
    (section) => section.id === conflict.sectionId
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90svh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Incompatible Selection
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              {allConflictingOptions.length === 1
                ? "The following option cannot be selected together with your current selection:"
                : `The following option conflicts with ${allConflictingOptions.length} of your current selections:`}
            </DialogDescription>
          </DialogHeader>

          <ConflictContent
            allSections={allSections}
            allConflictingOptions={allConflictingOptions}
            newOption={newOption}
            newOptionSection={newOptionSection}
          />

          <DialogFooter className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleReplace}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {allConflictingOptions.length === 1
                ? "Confirm Change"
                : "Replace All Conflicts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent>
        <SheetHeader className="text-left">
          <SheetTitle className="text-xl">Incompatible Selection</SheetTitle>
          <SheetDescription className="text-base pt-2">
            {allConflictingOptions.length === 1
              ? "The following option cannot be selected together with your current selection:"
              : `The following option conflicts with ${allConflictingOptions.length} of your current selections:`}
          </SheetDescription>
        </SheetHeader>

        <ConflictContent
          className="px-4"
          allSections={allSections}
          allConflictingOptions={allConflictingOptions}
          newOption={newOption}
          newOptionSection={newOptionSection}
        />

        <SheetFooter className="pt-2">
          <Button
            onClick={handleReplace}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {allConflictingOptions.length === 1
              ? "Confirm Change"
              : "Replace All Conflicts"}
          </Button>
          <SheetClose asChild>
            <Button variant="outline">Cancel</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default SelectionHelperDialog;
